import { useMemo, useState } from "react";
import { groupBlocks, usedByOthers } from "@/lib/blocks";
import { api } from "@/lib/api";
import { useData } from "@/lib/data";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { SupplierOpenOrders, DateNoteFields } from "@/components/SoleOrderFormParts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import SoleCodeBlock from "@/components/SoleCodeBlock";
import { Plus, Wand2 } from "lucide-react";
import { toast } from "sonner";
import SupplierPicker from "@/components/SupplierPicker";

import { emptySoleLine as emptyLine, soleLinesTotal, shortageToLines, soleOrderItems } from "@/lib/soleOrder";

export default function SoleOrderForm({ open, onOpenChange, onCreated, orders = [] }) {
  const { soleCards, suppliers: supplierCards } = useData();
  const [supplierId, setSupplierId] = useState("");
  const supplierCard = supplierCards.find((x) => x.id === supplierId);
  const supplier = supplierCard?.name || "";
  const setSupplierByName = (name) => { const f = supplierCards.find((x) => x.name === name); if (f) setSupplierId(f.id); };
  const openOrders = useMemo(() => orders.filter((o) => o.supplier === supplier && o.status !== "completed"), [orders, supplier]);
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);

  const blocks = useMemo(() => groupBlocks(lines), [lines]);
  const usedCodes = useMemo(() => usedByOthers(blocks, "code"), [blocks]);
  const setCode = (block, code) => {
    const cs = soleCards.filter((c) => c.sole_code === code);
    setLines((p) => {
      const at = p.findIndex((l) => l.block === block);
      const rest = p.filter((l) => l.block !== block);
      rest.splice(at, 0, { ...emptyLine(block, code), ...(cs.length === 1 ? { color: cs[0].sole_color || "", colorSet: true } : {}) });
      return rest;
    });
    if (!supplierId && cs[0]?.supplier) setSupplierByName(cs[0].supplier);
  };
  const addColor = (block, code) => setLines((p) => {
    const at = p.map((x) => x.block).lastIndexOf(block);
    const n = [...p]; n.splice(at + 1, 0, emptyLine(block, code)); return n;
  });
  const upd = (id, patch) => setLines((p) => p.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const total = useMemo(() => soleLinesTotal(lines), [lines]);

  const reset = () => { setSupplierId(""); setDate(""); setNote(""); setLines([emptyLine()]); };

  const fillShortage = async () => {
    const r = await api.get("/soles/shortage");
    if (!r.data.length) { toast.info("Eksik taban yok"); return; }
    const filled = shortageToLines(r.data);
    setLines(filled);
    const sups = [...new Set(filled.map((l) => soleCards.find((c) => c.sole_code === l.code && (c.sole_color || "") === l.color)?.supplier).filter(Boolean))];
    if (!supplierId && sups.length === 1) setSupplierByName(sups[0]);
    toast.success("Eksik tabanlar forma aktarıldı");
  };

  const submit = async () => {
    if (!supplier) { toast.error("Taban firması seçin"); return; }
    const { items, error } = soleOrderItems(lines);
    if (error) { toast.error(error); return; }
    const win = window.open("", "_blank");
    setSaving(true);
    try {
      const r = await api.post("/sole-orders", { supplier, supplier_id: supplierId, delivery_date: date, note, items });
      toast.success(`Taban siparişi #${r.data.no} oluşturuldu`);
      if (win) win.location.href = `/taban-siparis-fis/${r.data.id}`;
      reset(); onOpenChange(false); onCreated();
    } catch (e) {
      if (win) win.close();
      toast.error(e.response?.data?.detail || "Hata");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-[#FFFBF4] border-[#DCCFB8] text-[#2B2118] max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="sole-order-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight">Yeni Taban Siparişi</DialogTitle>
        </DialogHeader>
        <div>
          <Label className="text-xs text-[#6B5B4A] mb-1 block">Taban Firması *</Label>
          <SupplierPicker value={supplierId} onChange={setSupplierId} testId="sole-order-form-supplier" />
        </div>
        {supplier && <SupplierOpenOrders supplier={supplier} orders={openOrders} />}
        <DateNoteFields date={date} setDate={setDate} note={note} setNote={setNote} />
        <Button type="button" variant="outline" onClick={fillShortage} data-testid="sole-order-form-fill-shortage"
          className="w-fit border-dashed border-[#D97706]/60 bg-transparent text-[#B45309] hover:bg-[#EFE6D6] hover:text-[#B45309]">
          <Wand2 className="w-4 h-4 mr-2" /> Eksik Tabanları Getir
        </Button>
        {blocks.map((bl, idx) => (
          <SoleCodeBlock key={bl[0].block} idx={idx} lines={bl}
            usedCodes={usedCodes[idx]}
            onCode={(v) => setCode(bl[0].block, v)} onUpdate={upd}
            onAddColor={() => addColor(bl[0].block, bl[0].code)}
            onRemoveLine={(id) => setLines((p) => p.filter((x) => x.id !== id))}
            onRemoveBlock={blocks.length > 1 ? () => setLines((p) => p.filter((x) => x.block !== bl[0].block)) : null} />
        ))}
        <Button variant="outline" onClick={() => setLines((p) => [...p, emptyLine()])} data-testid="sole-order-add-line"
          className="w-fit border-[#DCCFB8] bg-[#EFE6D6] text-[#B45309] hover:bg-[#FFFBF4]">
          <Plus className="w-4 h-4 mr-2" /> Taban Ekle
        </Button>
        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <p className="font-mono text-xl font-bold text-[#B45309]" data-testid="sole-order-form-total">{total} çift</p>
            <Button onClick={submit} disabled={saving} data-testid="sole-order-submit-btn"
              className="bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide h-11 px-6">
              {saving ? "Kaydediliyor..." : "Siparişi Oluştur ve Yazdır"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
